import { SyntheticEvent, useEffect, useState } from 'react';

import Avatar from 'components/Avatar';
import { AvatarProps } from 'components/Avatar/Avatar.types';
import { IconClose } from 'components/Icons';

import { useAnalytics } from 'hooks/useAnalytics';
import { htmlId } from 'utils/html';

import { ANALYTICS } from 'analytics/constants';
import {
  AnalyticClickedHandraiserDetailsType,
  AnalyticToastShownDetailsType,
} from 'analytics/types';
import { LOCAL_STORAGE_KEY, STORAGE_LOCATION } from 'constants/browserCache';
import { CHAT_UI_PREFIX } from 'constants/id';
import { PARTICIPANT_NAME_SYSTEM_FRIENDLY } from 'constants/twilio';
import { useAuthentication } from 'hooks/authentication';
import { useCustomer } from 'hooks/customer';
import { getCookieItem, getStorageItem } from 'utils/browserCache';
import { getUserInfoContext } from 'utils/chatContext/getInfoContext';
import { DataDogLogger } from 'utils/dataDog';
import { CUSTOM_ACTION_NAMES } from 'utils/dataDog/constants';
import { getUserIdentifier } from 'utils/id';
import {
  AvatarWrapper,
  ChatNowButton,
  HandRaiserCloseWrapper,
  HandRaiserContainer,
  HandRaiserText,
  HandRaiserTextContainer,
} from './HandRaiserToast.styles';
import { HandRaiserToastProps } from './HandRaiserToast.types';

const avatarProps: AvatarProps = {
  participantName: PARTICIPANT_NAME_SYSTEM_FRIENDLY,
  showPresence: false,
  size: '3rem',
};

const HandRaiserToast = ({
  experimentName,
  handleStartChat,
  handleCloseToast,
  handraiserName,
  displayMessage,
  handleUpdateHandRaiserShown,
  openChatCTA,
}: HandRaiserToastProps) => {
  const { trackEvent } = useAnalytics();
  const { authenticationState } = useAuthentication();
  const { customerState } = useCustomer();

  const userFirstName = customerState?.userInfo?.firstName
    ? `${customerState.userInfo.firstName}, `
    : '';

  useEffect(() => {
    handleUpdateHandRaiserShown();
  }, []);

  // Used to track how long a user took to interact with the toast
  const [toastShownAt, setToastShownAt] = useState<Date>();
  const timeToAction = () => {
    return toastShownAt ? Date.now() - toastShownAt.getTime() : null;
  };

  const hrExperimentCookieValue = experimentName
    ? { hrExperimentCookieValue: getCookieItem(experimentName) }
    : {};

  useEffect(() => {
    setToastShownAt(new Date());
    trackEvent({
      analyticEvent: ANALYTICS.EVENT.TOAST_SHOWN,
      details: {
        content: displayMessage,
        toast_id: `hand-raiser`,
        handraiser_name: handraiserName,
      } as AnalyticToastShownDetailsType,
    });

    DataDogLogger.action(CUSTOM_ACTION_NAMES.HAND_RAISER_SHOWN, {
      pathname: window.location.pathname,
      ...hrExperimentCookieValue,
      userId: getUserInfoContext(
        { authenticationState, customerState },
        getStorageItem(STORAGE_LOCATION.LOCAL_STORAGE, LOCAL_STORAGE_KEY.CVNA_PROPEL_CHAT_CONTEXT),
      )?.user_id,
      ...getUserIdentifier(),
    });
  }, []);

  const onClickStartChat = (e: SyntheticEvent) => {
    handleStartChat(e);
    trackEvent({
      analyticEvent: ANALYTICS.EVENT.CLICKED,
      details: {
        analytic_click_id: `${CHAT_UI_PREFIX}-toast-hand-raiser`,
        content: displayMessage,
        handraiser_name: handraiserName,
      } as AnalyticClickedHandraiserDetailsType,
    });

    DataDogLogger.action(CUSTOM_ACTION_NAMES.HAND_RAISER_USER_CLICK, {
      pathname: window.location.pathname,
      timeToActionMs: timeToAction(),
      ...hrExperimentCookieValue,
      userId: getUserInfoContext(
        { authenticationState, customerState },
        getStorageItem(STORAGE_LOCATION.LOCAL_STORAGE, LOCAL_STORAGE_KEY.CVNA_PROPEL_CHAT_CONTEXT),
      )?.user_id,
      ...getUserIdentifier(),
    });
  };

  const onClickCloseToast = (e: SyntheticEvent) => {
    handleCloseToast(e);
    trackEvent({
      analyticEvent: ANALYTICS.EVENT.CLICKED,
      details: {
        analytic_click_id: `${CHAT_UI_PREFIX}-toast-hand-raiser-close-button`,
        content: 'X',
        handraiser_name: handraiserName,
      } as AnalyticClickedHandraiserDetailsType,
    });

    DataDogLogger.action(CUSTOM_ACTION_NAMES.HAND_RAISER_CLOSED, {
      pathname: window.location.pathname,
      timeToActionMs: timeToAction(),
      ...hrExperimentCookieValue,
      userId: getUserInfoContext(
        { authenticationState, customerState },
        getStorageItem(STORAGE_LOCATION.LOCAL_STORAGE, LOCAL_STORAGE_KEY.CVNA_PROPEL_CHAT_CONTEXT),
      )?.user_id,
      ...getUserIdentifier(),
    });
  };

  return (
    <HandRaiserContainer id={htmlId('toast-hand-raiser-container')}>
      <AvatarWrapper>
        <Avatar {...avatarProps} />
      </AvatarWrapper>
      <HandRaiserTextContainer>
        <HandRaiserText>{`${userFirstName}${displayMessage}`}</HandRaiserText>
        <ChatNowButton onClick={onClickStartChat}>{openChatCTA}</ChatNowButton>
      </HandRaiserTextContainer>
      <HandRaiserCloseWrapper onClick={onClickCloseToast}>
        <IconClose />
      </HandRaiserCloseWrapper>
    </HandRaiserContainer>
  );
};

export default HandRaiserToast;
